import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import type { SortingState } from "@tanstack/react-table";
import { toast } from "sonner";
import { BUCKET_ORDER, type ForecastBucket, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { SearchField } from "@/components/app/patterns/search-field";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { saveFile } from "@/lib/files/download";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { useFacility } from "@/lib/facility/facility";
import { optionalOneOf, optionalString, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { orpc } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { useCan } from "@/lib/policy";
import { Notice } from "@/routes/_authenticated/_pharmacy/-components/notice";
import { ForecastBucketCard } from "./-components/forecast-bucket";
import { ForecastMenu } from "./-components/forecast-menu";
import { ForecastSummary } from "./-components/forecast-summary";
import type { ForecastRow } from "./-components/forecast-row";

const HORIZON_DAYS = ["14", "60", "90"] as const;
type ForecastSearch = { days?: (typeof HORIZON_DAYS)[number]; q?: string; pharmacy?: string };

export const Route = createFileRoute("/_authenticated/_pharmacy/supply-forecast/")({
  validateSearch: (search: Record<string, unknown>): ForecastSearch => ({
    days: optionalOneOf(HORIZON_DAYS, optionalString(search.days)),
    q: optionalSearch(search.q),
    pharmacy: optionalString(search.pharmacy)?.slice(0, 60) || undefined,
  }),
  component: SupplyForecast,
});

const HORIZONS = [14, 30, 60, 90].map((count) => ({
  value: String(count),
  label: `${count} days`,
  ariaLabel: `Next ${count} days`,
}));

/** What runs out, and when somebody has to move on it. */
function SupplyForecast() {
  const { facilityId } = useFacility();
  const canManage = useCan("medication:manage");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ForecastSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const horizonDays = Number(params.days ?? 30);
  const setHorizonDays = (next: number) =>
    patch({ days: optionalOneOf(HORIZON_DAYS, String(next)) });
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  const pharmacy = params.pharmacy ?? "all";
  const setPharmacy = (next: string) => patch({ pharmacy: next === "all" ? undefined : next });
  /* One sort for every bucket table: unsorted leaves them in act-by order. */
  const [sorting, setSorting] = useState<SortingState>([]);

  const forecast = useQuery(
    orpc.pharmacy.supply.forecast.queryOptions({ input: { facilityId, horizonDays } }),
  );

  // The printed list is grouped by when somebody has to act and ends with the pharmacy call list;
  // the CSV is the same rows in the same order, for sorting and pasting into an order. A nurse
  // working the telephone wants the first, a manager filling in a purchase wants the second.
  const download = useMutation(
    orpc.pharmacy.supply.forecastPdf.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const csv = useMutation(
    orpc.pharmacy.supply.forecastCsv.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const request = useMutation(
    orpc.pharmacy.supply.requestRefill.mutationOptions({
      onSuccess: async (_result, variables) => {
        toast.success(variables.clear ? "Refill request cleared" : "Marked as requested");
        await invalidateKeys(queryClient, [orpc.pharmacy.key(), orpc.care.residents.orders.key()]);
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = (forecast.data?.items ?? []).filter((row) => {
      if (pharmacy !== "all" && (row.pharmacyId ?? "none") !== pharmacy) return false;
      if (!term) return true;
      return (
        row.residentName.toLowerCase().includes(term) ||
        row.orderName.toLowerCase().includes(term) ||
        (row.roomNumber ?? "").toLowerCase().includes(term)
      );
    });
    const byBucket = new Map<ForecastBucket, ForecastRow[]>();
    for (const row of rows) {
      const list = byBucket.get(row.bucket) ?? [];
      list.push(row);
      byBucket.set(row.bucket, list);
    }
    return BUCKET_ORDER.filter((bucket) => byBucket.has(bucket)).map((bucket) => ({
      bucket,
      rows: byBucket.get(bucket)!,
    }));
  }, [forecast.data, search, pharmacy]);

  const untracked = forecast.data?.untrackedCount ?? 0;
  const calls = forecast.data?.byPharmacy ?? [];

  return (
    <>
      <PageHeader
        title="Supply forecast"
        meta="Sorted by when you have to start, not when the bottle runs out."
        actions={
          <>
            {/* The register has no entry in the rail: this is its only way in. */}
            <Button asChild variant="outline">
              <Link to="/medications">All medications</Link>
            </Button>
            <ForecastMenu
              exporting={download.isPending || csv.isPending}
              onExport={(format) =>
                (format === "csv" ? csv : download).mutate({ facilityId, horizonDays })
              }
            />
          </>
        }
      />

      {/* The blind spot must be a headline, not a footnote: an order with no
          count cannot be forecast, so a report that hides it claims all-clear. */}
      {untracked > 0 ? (
        <Notice
          tone="warn"
          title={`${untracked === 1 ? "1 order has" : `${untracked} orders have`} no count on hand`}
          action={
            <Button asChild size="sm" variant="outline">
              <Link to="/pharmacy-receiving" search={{ mode: "count" }}>
                Start a count
              </Link>
            </Button>
          }
        >
          <p>
            Nothing on this page can predict a run-out for them, so they are missing from every
            number here. Do a count before treating this forecast as complete.
          </p>
        </Notice>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <ToggleGroup
          type="single"
          variant="outline"
          aria-label="Forecast horizon"
          value={String(horizonDays)}
          onValueChange={(next) => {
            const picked = HORIZONS.find((option) => option.value === next);
            if (picked) setHorizonDays(Number(picked.value));
          }}
        >
          {HORIZONS.map((option) => (
            <ToggleGroupItem key={option.value} value={option.value} aria-label={option.ariaLabel}>
              {option.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Select value={pharmacy} onValueChange={setPharmacy}>
          <SelectTrigger className="w-52" aria-label="Pharmacy">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All pharmacies</SelectItem>
            {calls.map((entry) => (
              <SelectItem key={entry.pharmacyId ?? "none"} value={entry.pharmacyId ?? "none"}>
                {entry.pharmacyName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <SearchField
          className="sm:w-72"
          value={search}
          onChange={setSearch}
          label="Find a resident, room or medication"
          placeholder="Resident, room or medication…"
        />
      </div>

      {forecast.data ? (
        <ForecastSummary buckets={forecast.data.buckets} calls={calls} horizonDays={horizonDays} />
      ) : null}

      {forecast.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : forecast.isError ? (
        /* "Nothing running out" here would be an all-clear nobody gave. */
        <Card>
          <CardContent className="p-4">
            <ReadFailed
              message="Couldn't load the forecast. Nothing is shown because nothing was read."
              error={forecast.error}
              retrying={forecast.isFetching}
              onRetry={() => void forecast.refetch()}
            />
          </CardContent>
        </Card>
      ) : grouped.length === 0 && (forecast.data?.items.length ?? 0) > 0 ? (
        <Card className="gap-0 py-0">
          <EmptyState
            title="Nothing matches these filters"
            description="Clear the search or pick all pharmacies to see the whole forecast."
            action={
              <Button
                variant="outline"
                onClick={() => {
                  setSearch("");
                  setPharmacy("all");
                }}
              >
                Clear filters
              </Button>
            }
          />
        </Card>
      ) : grouped.length === 0 ? (
        <Card className="gap-0 py-0">
          <EmptyState
            title="Nothing running out"
            description={`Nothing needs action inside the next ${horizonDays} days.`}
          />
        </Card>
      ) : (
        grouped.map(({ bucket, rows }) => (
          <ForecastBucketCard
            key={bucket}
            bucket={bucket}
            rows={rows}
            canManage={canManage}
            pending={request.isPending}
            sorting={sorting}
            onSortingChange={setSorting}
            onRequest={(orderId, clear) => request.mutate({ orderId, clear })}
          />
        ))
      )}
    </>
  );
}
